import React from 'react';
import { firstCommunions as api } from '../../api/endpoints';
import { formatDate } from '../../utils/format';
import { FormLayout, Input } from '../../components/ui';
import type { RegisterConfig } from './registerConfig';
import RegisterMemberField from './MemberField';
import type { FirstCommunion, Member } from '../../types';

// First Holy Communion — the stage between baptism and confirmation, and the
// one the parish had no register for. Saving an entry sets the member's
// communion date and moves their status to Received, so the Sacraments list
// stops showing them as due.

const EMPTY = {
  communionNo: '', communionRegisterNo: '', memberId: '', personName: '',
  baptismReference: '', communionDate: '', minister: '', church: '',
  certificateNo: '', remarks: '',
  /** Form-local: the picked member, for the warnings. Stripped before saving. */
  _member: null as Member | null
};

type Form = typeof EMPTY;

const Certificate = (r: FirstCommunion) => (
  <div>
    <p>
      This is to certify that <strong>{r.personName || r.memberId || '—'}</strong> received
      the Sacrament of the Lord&apos;s Supper for the first time on{' '}
      <strong>{formatDate(r.communionDate)}</strong> at <strong>{r.church || '—'}</strong>,
      administered by <strong>{r.minister || '—'}</strong>.
    </p>

    <div style={{ marginTop: '20px' }}>
      {r.memberId && <div><strong>Member ID:</strong> {r.memberId}</div>}
      <div><strong>Baptism Reference:</strong> {r.baptismReference || '—'}</div>
      <div><strong>Certificate No:</strong> {r.certificateNo || '—'}</div>
      {r.remarks && <div><strong>Remarks:</strong> {r.remarks}</div>}
    </div>

    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '70px' }}>
      <div style={{ borderTop: '1px solid #000', width: '220px', textAlign: 'center', paddingTop: '5px' }}>Minister</div>
      <div style={{ borderTop: '1px solid #000', width: '220px', textAlign: 'center', paddingTop: '5px' }}>Church Seal</div>
    </div>
  </div>
);

/** Asked, never enforced — a member who transferred in has gaps by definition. */
const warn = (f: Form): string | null => {
  const m = f._member;
  if (!m) return null;

  const notes: string[] = [];
  if (!m.baptismDate) {
    notes.push(
      `${m.name || m.memberId} has no baptism on file — communion normally follows baptism. `
      + 'That is expected for a member who transferred in from another church.'
    );
  }
  if (m.communionDate && m.communionDate !== f.communionDate) {
    notes.push(
      `${m.name || m.memberId} already received first communion on ${formatDate(m.communionDate)}. `
      + 'Saving will replace that date on the member record.'
    );
  }
  return notes.length ? notes.join(' ') : null;
};

const config: RegisterConfig<FirstCommunion, Form> = {
  icon: '🍞',
  title: 'First Communion Register',
  prefix: 'FCM',
  label: 'Communion Entry',
  resource: api,
  emptyForm: EMPTY,
  toForm: (r) => ({
    ...EMPTY,
    communionNo: r.communionNo || '', communionRegisterNo: r.communionRegisterNo || '',
    memberId: r.memberId || '', personName: r.personName || '',
    baptismReference: r.baptismReference || '', communionDate: r.communionDate || '',
    minister: r.minister || '', church: r.church || '',
    certificateNo: r.certificateNo || '', remarks: r.remarks || ''
  }),
  validate: (f) => {
    if (!f.memberId.trim() && !f.personName.trim()) {
      return 'Choose the member, or type the name if they are not on the rolls.';
    }
    if (!f.communionDate) return 'The date of first communion is required.';
    if (!f.minister.trim()) return 'The officiating minister is required.';
    const born = f._member?.dateOfBirth;
    if (born && f.communionDate < born) {
      return 'The communion cannot be dated before the member was born.';
    }
    return null;
  },
  warn,
  rowDate: (r) => r.communionDate,
  searchKeys: ['recordId', 'memberId', 'personName', 'communionNo', 'communionRegisterNo', 'minister', 'certificateNo'],
  searchPlaceholder: 'Search by name, Member ID, Communion No or Minister...',
  columns: [
    {
      key: 'recordId',
      header: 'Date & Comm. No.',
      nowrap: true,
      render: (r: FirstCommunion) => (
        <>
          <strong>{formatDate(r.communionDate)}</strong>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.communionNo || r.recordId}</div>
        </>
      )
    },
    {
      key: 'personName',
      header: 'Member',
      render: (r: FirstCommunion) => (
        <>
          <strong>{r.personName || r.memberId || '-'}</strong>
          {r.personName && <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.memberId}</div>}
        </>
      )
    },
    { key: 'baptismReference', header: 'Baptism Ref.', render: (r: FirstCommunion) => r.baptismReference || '-' },
    {
      key: 'minister',
      header: 'Minister & Church',
      render: (r: FirstCommunion) => (
        <>
          <div>{r.minister || '-'}</div>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.church || '-'}</div>
        </>
      )
    },
    { key: 'certificateNo', header: 'Cert No.', render: (r: FirstCommunion) => r.certificateNo || '-' }
  ],
  printModule: 'Baptism',
  printDocumentType: 'COMMUNION_CERTIFICATE',
  listDocumentType: 'COMMUNION_REGISTER',
  printData: (r: any) => ({ ...r, dob: formatDate(r.dob), baptismDate: formatDate(r.date || r.communionDate) }),
  renderDocument: Certificate,
  fields: (form, setForm, ctx) => (
    <>
      <FormLayout.Grid>
        <RegisterMemberField
          form={form}
          setForm={setForm}
          idKey="memberId"
          memberKey="_member"
          label="Member Receiving Communion"
          hint="Clear the box and type the name below if they are not on the rolls."
          fullWidth
          isDarkMode={ctx.isDarkMode}
          autofill={(member) => ({
            personName: member?.name || '',
            baptismReference: member?.baptismDate
              ? `Baptised ${formatDate(member.baptismDate)}`
              : ''
          })}
        />
        <Input
          label="Name as Registered" required placeholder="e.g. Grace Anna"
          value={form.personName} onChange={(e) => setForm({ ...form, personName: e.target.value })}
        />
        <Input
          label="Date of First Communion" type="date" required
          value={form.communionDate} onChange={(e) => setForm({ ...form, communionDate: e.target.value })}
        />
        <Input
          label="Officiating Minister" required placeholder="e.g. Rev. David Thomas"
          value={form.minister} onChange={(e) => setForm({ ...form, minister: e.target.value })}
        />
        <Input
          label="Church" placeholder="Where the communion was received"
          value={form.church} onChange={(e) => setForm({ ...form, church: e.target.value })}
        />
        <Input
          label="Baptism Reference" fullWidth
          placeholder="The baptism register entry this follows from"
          value={form.baptismReference} onChange={(e) => setForm({ ...form, baptismReference: e.target.value })}
        />
      </FormLayout.Grid>

      <FormLayout.Section title="Register Details" />
      <FormLayout.Grid>
        <Input
          label="Communion Number" placeholder="e.g. FC-2026-011"
          value={form.communionNo} onChange={(e) => setForm({ ...form, communionNo: e.target.value })}
        />
        <Input
          label="Communion Register No." placeholder="e.g. Vol 1, Page 8"
          value={form.communionRegisterNo} onChange={(e) => setForm({ ...form, communionRegisterNo: e.target.value })}
        />
        <Input
          label="Certificate Number" placeholder="e.g. FCC-2026-011"
          value={form.certificateNo} onChange={(e) => setForm({ ...form, certificateNo: e.target.value })}
        />
        <Input
          label="Remarks" type="textarea" rows={2} fullWidth
          value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
        />
      </FormLayout.Grid>
    </>
  )
};

export { config as firstCommunionConfig };
